import React from "react";

import { parse } from "cookie";
import { createRoot, hydrateRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";

import { initUFO } from "@/analytics/react-ufo/initUFO";
import { getAnalyticsWebClient } from "@/analytics/wac-analytics/get-analytics-web-client";
import {
    IntercomProperties,
    IntercomSettingsProperties
} from "@/libraries/intercom/src";
import { isPerformanceOnetrustEnabled } from "@/utils/onetrust-provider";

import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { CustomerStory } from "./content/customerStories/customerStories.types";
import {
    TemplateCollection,
    type Template
} from "./content/templates/template.types";
import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import {
    AvailableProductsResponse,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src";
import { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client";
import { PageWrapper } from "./page-wrapper";
import { AgileTutorial } from "./templates/agile-hub/tutorial-types";
import { AgileArticle, AgileOverview } from "./templates/agile-hub/types";
import { CustomersLanding } from "./templates/customers/customers-landing-page/utils";
import { AnchorContentsResponse } from "./utils/anchor-queries/get-anchor-contents";
import {
    overrideAjsAnonIdFromCookie,
    overrideAtlassianAccountIdFromCookie,
    overrideCustomAttributes
} from "./utils/fg-bootstrap-values-override-utils";
import { GridOverlayFunction } from "./utils/grid-overlay";
import { SentryIntlProvider } from "./utils/sentry-intl";
import { UserLocale } from "./utils/useGeoCurrency";
import { getExperimentOverrides } from "./utils/wac-browser-utility";
import { parseRoute } from "./wac-routes";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    type FeatureFlagType = "boolean" | "string" | "number";

    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        entryName?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AvailableProductsResponse;
        userProfile?: UserProfile;
        userProfileStatus?: ServerSideRequestStatus;
        userContext?: UserProfileResponse;
        userContextStatus?: ServerSideRequestStatus;
        loggedOutContext?: GrowthUnifiedProfile;
        loggedOutContextStatus?: ServerSideRequestStatus;
        agileArticle?: AgileArticle;
        agileOverview?: AgileOverview;
        tutorial?: AgileTutorial;
        cmsPageVariant?: string;
        customersLanding?: CustomersLanding;
        customerStory?: CustomerStory;
        templates?: Template[];
        mlTemplateIds?: string[];
        partnerTemplates?: Template[];
        collections?: TemplateCollection[];
        categories?: TemplateCategory[];
        anchorContents?: AnchorContentsResponse;
        persona?: string;
        teamType?: string;
        keyword?: string;
        featureFlags: Record<string, FeatureFlagType>;
        countryCode?: UserLocale;
        AtlOneTrustWrapper?: { updatePolicyLinks: () => void };
        Intercom?: IntercomProperties;
        intercomSettings?: IntercomSettingsProperties;
        toggleGrid?: GridOverlayFunction;
        isUniformPlayground?: boolean;
        s2sExperimentCallMade?: boolean;
        referrer?: string;
        track?: string;
        useEsmodules?: boolean;
        anchor?: string;
        subAnchor?: string;
    }
}

const regexPattern =
    "^(?:/$|" +
    "/(?:br|de|en|es|fr|it|ja|ko|nl|pl|ru|zh)(?:/software/jira/product-discovery)?|" +
    "/software/jira/product-discovery)$";
const edgeCachingPathRegex = new RegExp(regexPattern);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
    const isStatic = typeof window !== "undefined" && window.IS_STATIC;
    const analyticsWebClient = getAnalyticsWebClient();
    const { locale, path } = parseRoute(window.location);
    isPerformanceOnetrustEnabled() && initUFO(analyticsWebClient, path);

    const parsedRequestCookies = document.cookie ? parse(document.cookie) : {};
    // @ts-ignore
    const experimentOverrides = getExperimentOverrides(parsedRequestCookies);

    // We can't feature gate below code as this is done before initialization so just overriding for the path we are testing
    if (edgeCachingPathRegex.test(window.location.pathname)) {
        // Override for Edge Caching
        overrideAjsAnonIdFromCookie();
        overrideAtlassianAccountIdFromCookie();
        overrideCustomAttributes();
    }

    initializeFeatureGatesClient(window.featureGateBootstrapValues, {
        overrideValues: experimentOverrides
    }).then(() => {
        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <PageWrapper
                        analyticsWebClient={analyticsWebClient}
                        pureClientMode={true}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        userProfile={window.userProfile}
                        userProfileStatus={window.userProfileStatus}
                        userContext={window.userContext}
                        userContextStatus={window.userContextStatus}
                        loggedOutContext={window.loggedOutContext}
                        loggedOutContextStatus={window.loggedOutContextStatus}
                        countryCode={window.countryCode}
                        agileArticle={window.agileArticle}
                        agileOverview={window.agileOverview}
                        tutorial={window.tutorial}
                        customersLanding={window.customersLanding}
                        customerStory={window.customerStory}
                        templates={window.templates}
                        partnerTemplates={window.partnerTemplates}
                        collections={window.collections}
                        categories={window.categories}
                        composition={window._uniformPreloadedComposition}
                        isUniformPlayground={window.isUniformPlayground}
                        anchorContents={window.anchorContents}
                        persona={window.persona}
                        teamType={window.teamType}
                        keyword={window.keyword}
                        referrer={window.referrer}
                        mlTemplateIds={window.mlTemplateIds}
                        track={window.track}
                        anchor={window.anchor}
                        subAnchor={window.subAnchor}
                    >
                        {Component}
                    </PageWrapper>
                </SentryIntlProvider>
            );
        } else {
            hydrateRoot(
                // @ts-ignore
                document.getElementById("wac-root"),
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <Layout
                        analyticsWebClient={analyticsWebClient}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        agileArticle={window.agileArticle}
                        agileOverview={window.agileOverview}
                        tutorial={window.tutorial}
                        customersLanding={window.customersLanding}
                        customerStory={window.customerStory}
                        templates={window.templates}
                        partnerTemplates={window.partnerTemplates}
                        collections={window.collections}
                        categories={window.categories}
                        userProfile={window.userProfile}
                        userProfileStatus={window.userProfileStatus}
                        userContext={window.userContext}
                        userContextStatus={window.userContextStatus}
                        loggedOutContext={window.loggedOutContext}
                        loggedOutContextStatus={window.loggedOutContextStatus}
                        countryCode={window.countryCode}
                        anchorContents={window.anchorContents}
                        composition={window._uniformPreloadedComposition}
                        isUniformPlayground={window.isUniformPlayground}
                        persona={window.persona}
                        teamType={window.teamType}
                        keyword={window.keyword}
                        mlTemplateIds={window.mlTemplateIds}
                        referrer={window.referrer}
                        anchor={window.anchor}
                        subAnchor={window.subAnchor}
                    >
                        {Component}
                    </Layout>
                </SentryIntlProvider>,
                {
                    onRecoverableError: (error, errorInfo) => {
                        // add an attribute if in CI
                        if (process.env["CI"] === "true") {
                            document
                                .getElementById("wac-root")
                                ?.setAttribute("hydration-error", "true");
                        }
                        if (process.env["MODE"] === "dev") {
                            console.log(
                                `Recoverable Error (including Hydration): ${error}`,
                                errorInfo
                            );
                        }
                    }
                }
            );
        }
    });
};
